import React, { useState, useEffect, useCallback } from 'react';
import { outcomeSpecsApi } from '../../utils/api';
import SuggestionControl from './SuggestionControl';

// Pick the open suggestion for a target, else the latest resolved.
function pickSug(suggestions, deliverableId, blockType) {
  const m = (suggestions || []).filter((s) => s.target_type === 'spec_block' && s.target_deliverable_id === deliverableId && s.target_block_type === blockType);
  return m.find((s) => s.status === 'open') || m[m.length - 1] || null;
}

// PRD-020 F3 — full-width Outcome Spec panel: the five typed blocks render as
// labeled cards in a responsive grid (repeat(auto-fit, minmax(232px,1fr)))
// that reflows 3→2→1 as the viewport narrows. NO horizontal scroll, ever.
// Each card edits in place (click → textarea → blur saves) via the direct
// seller content-save endpoint (PRD-028 F1: PUT .../blocks/:blockId). Read-only
// for the buyer / public view. Save errors surface inline via apiErrorMessage.

const BLOCK_TYPES = ['definition', 'success_criteria', 'required_artifacts', 'verification_method', 'exclusions'];
const BLOCK_LABELS = {
  definition: 'Definition',
  success_criteria: 'Success criteria',
  required_artifacts: 'Required artifacts',
  verification_method: 'Verification method',
  exclusions: 'Exclusions',
};
const REQUIRED_BLOCKS = ['definition', 'success_criteria'];

function apiErrorMessage(err, fallback) {
  return (
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.response?.data?.errors?.[0]?.msg ||
    fallback
  );
}

export default function SpecPanel({ proposalId, deliverableId, editable, actorSide = 'seller', suggestions = [], suggestionHandlers, mySide = 'seller', canSuggest = false }) {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // block_type being edited
  const [draftText, setDraftText] = useState('');
  const [error, setError] = useState(null);
  const [savingType, setSavingType] = useState(null);

  const fetchSpec = useCallback(async () => {
    if (!deliverableId) { setLoading(false); return; }
    try {
      const res = await outcomeSpecsApi.getProposalSpec(proposalId, deliverableId);
      setSpec(res.data.spec || res.data);
    } catch {
      setSpec(null);
    } finally { setLoading(false); }
  }, [proposalId, deliverableId]);

  useEffect(() => { fetchSpec(); }, [fetchSpec]);

  const blockMap = Object.fromEntries((spec?.blocks || []).map((b) => [b.block_type, b]));

  const startEdit = (bt, current) => {
    if (!editable) return;
    setError(null);
    setEditing(bt);
    setDraftText(current || '');
  };

  const saveEdit = async (block) => {
    const bt = block.block_type;
    const original = block.content_markdown || '';
    setEditing(null);
    if (draftText === original || !block.id) return; // no-op or no real block id yet
    setSavingType(bt);
    try {
      await outcomeSpecsApi.saveBlock(proposalId, deliverableId, block.id, { content_markdown: draftText });
      await fetchSpec();
    } catch (err) {
      setError(apiErrorMessage(err, `Failed to save ${BLOCK_LABELS[bt]}`));
    } finally { setSavingType(null); }
  };

  if (loading) return <div className="text-xs text-gray-600 px-1 py-2">Loading spec…</div>;

  return (
    <div>
      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(232px, 1fr))' }}>
        {BLOCK_TYPES.map((bt) => {
          const block = blockMap[bt] || { id: null, block_type: bt, content_markdown: '' };
          const isRequired = REQUIRED_BLOCKS.includes(bt);
          const value = block.content_markdown || '';
          const blockSug = pickSug(suggestions, deliverableId, bt);
          return (
            // PRD-027 WU-C — `group relative` enables the hover-revealed "+ Suggest"
            // affordance; an open suggestion ring-highlights the block.
            <div key={bt} className={`group relative border rounded-lg p-2.5 bg-gray-50 ${blockSug && blockSug.status === 'open' ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-200'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-600">{BLOCK_LABELS[bt]}</span>
                {isRequired && <span className="text-[10px] text-coral-700">required</span>}
                {savingType === bt && <span className="text-[10px] text-gray-600">saving…</span>}
                <span className="ml-auto">
                  <SuggestionControl target={{ type: 'spec_block', deliverableId, blockType: bt }} suggestion={blockSug}
                    side={mySide} canSuggest={canSuggest} currentValue={value} handlers={suggestionHandlers} />
                </span>
              </div>
              {editing === bt ? (
                <textarea
                  autoFocus
                  className="w-full text-xs border border-primary-400 rounded p-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  style={{ minHeight: 64, resize: 'vertical' }}
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  onBlur={() => saveEdit(block)}
                />
              ) : (
                <div
                  className={`text-xs leading-relaxed whitespace-pre-wrap ${value ? 'text-gray-700' : 'text-gray-600 italic'} ${editable ? 'cursor-text' : ''}`}
                  style={{ minHeight: 18 }}
                  onClick={() => startEdit(bt, value)}
                >
                  {value || (editable ? 'Click to add…' : '—')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
